import { Button } from 'antd';
import { ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getItemWithExpiry } from '@wavvest/shared-utils';

import { getEntitlementStatus } from './api';
import {
  rememberSubscriptionReturnPath,
  shouldCheckEntitlement,
  SUBSCRIPTION_RECOVERY_PATH,
  SUBSCRIPTION_REQUIRED_EVENT,
} from './navigation';
import { RouteLoadingFallback } from '@/routes/components/route-loading-fallback';

type CheckState = 'checking' | 'allowed' | 'unavailable';

type EntitlementCheckResult = {
  routeKey: string | null;
  state: CheckState;
};

const SubscriptionEntitlementBoundary = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const authenticated = Boolean(getItemWithExpiry('access_token'));
  const needsCheck = shouldCheckEntitlement(location.pathname, authenticated);
  const routeKey = `${location.pathname}${location.search}${location.hash}`;
  const [checkResult, setCheckResult] = useState<EntitlementCheckResult>(() => ({
    routeKey: needsCheck ? routeKey : null,
    state: needsCheck ? 'checking' : 'allowed',
  }));
  const [retrySequence, setRetrySequence] = useState(0);
  const checkState =
    !needsCheck || checkResult.routeKey === routeKey ? checkResult.state : 'checking';

  useEffect(() => {
    const handleSubscriptionRequired = () => {
      if (location.pathname === SUBSCRIPTION_RECOVERY_PATH) {
        return;
      }
      rememberSubscriptionReturnPath(`${location.pathname}${location.search}${location.hash}`);
      navigate(SUBSCRIPTION_RECOVERY_PATH, { replace: true });
    };

    window.addEventListener(SUBSCRIPTION_REQUIRED_EVENT, handleSubscriptionRequired);
    return () =>
      window.removeEventListener(SUBSCRIPTION_REQUIRED_EVENT, handleSubscriptionRequired);
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!needsCheck) {
      setCheckResult({
        routeKey: null,
        state: 'allowed',
      });
      return;
    }

    let active = true;
    setCheckResult({
      routeKey,
      state: 'checking',
    });
    getEntitlementStatus()
      .then((status) => {
        if (!active) return;
        if (status.entitled) {
          setCheckResult({
            routeKey,
            state: 'allowed',
          });
          return;
        }
        rememberSubscriptionReturnPath(`${location.pathname}${location.search}${location.hash}`);
        navigate(SUBSCRIPTION_RECOVERY_PATH, { replace: true });
      })
      .catch(() => {
        if (active) {
          setCheckResult({
            routeKey,
            state: 'unavailable',
          });
        }
      });

    return () => {
      active = false;
    };
  }, [location.hash, location.pathname, location.search, navigate, needsCheck, retrySequence, routeKey]);

  if (!needsCheck || checkState === 'allowed') {
    return <>{children}</>;
  }

  if (checkState === 'checking') {
    return <RouteLoadingFallback />;
  }

  return (
    <main style={{ margin: '15vh auto', maxWidth: 560, padding: 32, textAlign: 'center' }}>
      <h1>We could not verify your subscription</h1>
      <p>
        Your workspace has not been unlocked because the subscription service is temporarily
        unavailable. Please try again.
      </p>
      <Button type="primary" onClick={() => setRetrySequence((value) => value + 1)}>
        Try again
      </Button>
    </main>
  );
};

export default SubscriptionEntitlementBoundary;
